/**
 * UploadPickMenu — ЕДИНОЕ меню источника файла:
 *   [⬆ Загрузить фото/видео]  [🖼 Из моих генераций]
 *
 * Один компонент для обоих баров (ноль дрейфа, один дебаг): рендерится
 * порталом с fixed-позицией у слота загрузки через useUploadPickerMenu
 * (GenerateInputBar и GenerateToolMode используют один и тот же хук).
 *
 * Компонент чисто презентационный: закрытие (outside-click/backdrop) —
 * ответственность вызывающего, здесь только разметка и колбэки.
 */
import { useTranslation } from 'react-i18next'
import type { AssetKind } from '../../../api/assets'

/** Шапка меню: для кого выбираем (18.10, «Повторить движение»: кадр человека из ролика и подпись). */
export interface PickMenuContext {
	image?: string | null
	label: string
	caption?: string
}

interface Props {
	kind: 'images' | 'videos' | 'audios'
	onPickFile: () => void
	onPickGallery: () => void
	/** Для кого выбираем — шапка над пунктами; нет — меню как раньше. */
	context?: PickMenuContext | null
	/** Пункты сцены «Персонаж / Локация / Предмет» — только для фото-слотов авторизованного юзера;
	 *  scene=false — только персонаж (слот тренда: туда встаёт герой). */
	onPickScene?: (kind: AssetKind) => void
	sceneKinds?: AssetKind[]
	className?: string
	style?: React.CSSProperties
}

const SCENE_ITEMS: { kind: AssetKind; label: string; icon: React.ReactNode }[] = [
	{
		kind: 'character',
		label: 'Аватар',
		icon: (
			<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
				<circle cx="12" cy="8" r="3.4" />
				<path d="M5 20c1.3-3.4 3.9-5 7-5s5.7 1.6 7 5" />
			</svg>
		),
	},
	{
		kind: 'location',
		label: 'Локация',
		icon: (
			<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
				<path d="M3 19 9.5 9l4 6 2.5-3.5L21 19Z" />
				<circle cx="16.5" cy="6.5" r="1.8" />
			</svg>
		),
	},
	{
		kind: 'prop',
		label: 'Предмет',
		icon: (
			<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
				<path d="M12 3 20 7.5v9L12 21l-8-4.5v-9Z" />
				<path d="M4 7.5 12 12l8-4.5M12 12v9" />
			</svg>
		),
	},
]

export default function UploadPickMenu({ kind, onPickFile, onPickGallery, onPickScene, sceneKinds, context, className = '', style }: Props) {
	const { t } = useTranslation()
	const video = kind === 'videos'
	const audio = kind === 'audios'
	return (
		<div className={`upload-picker${className ? ` ${className}` : ''}`} style={style} role="menu">
			{context && (
				<div className="upload-picker__who">
					{context.image && <img src={context.image} alt="" />}
					<span className="upload-picker__who-text">
						{context.caption && <small>{context.caption}</small>}
						<b>{context.label}</b>
					</span>
				</div>
			)}
			<button className="upload-picker__item" type="button" onClick={onPickFile}>
				<svg
					width="14"
					height="14"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
					aria-hidden="true"
				>
					<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
					<polyline points="17 8 12 3 7 8" />
					<line x1="12" y1="3" x2="12" y2="15" />
				</svg>
				{audio
					? t('generate.upload.uploadAudio')
					: video
						? t('generate.upload.uploadVideo')
						: t('generate.upload.uploadPhoto')}
			</button>
			<button className="upload-picker__item" type="button" onClick={onPickGallery}>
				{audio ? (
					<svg
						width="14"
						height="14"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						aria-hidden="true"
					>
						<path d="M9 18V5l12-2v13" />
						<circle cx="6" cy="18" r="3" />
						<circle cx="18" cy="16" r="3" />
					</svg>
				) : video ? (
					<svg
						width="14"
						height="14"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						aria-hidden="true"
					>
						<polygon points="23 7 16 12 23 17 23 7" />
						<rect x="1" y="5" width="15" height="14" rx="2" />
					</svg>
				) : (
					<svg
						width="14"
						height="14"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						aria-hidden="true"
					>
						<rect x="3" y="3" width="18" height="18" rx="2" />
						<circle cx="8.5" cy="8.5" r="1.5" />
						<polyline points="21 15 16 10 5 21" />
					</svg>
				)}
				{t('generate.upload.fromGallery')}
			</button>
			{onPickScene &&
				SCENE_ITEMS.filter((it) => !sceneKinds || sceneKinds.includes(it.kind)).map((it) => (
					<button key={it.kind} className="upload-picker__item" type="button" onClick={() => onPickScene(it.kind)}>
						<span className="upload-picker__ico" aria-hidden="true">{it.icon}</span>
						{it.label}
						{/* Акцент на новой фиче (владелец 06.09: бейдж живёт в меню фото-слота). */}
						<span className="upload-picker__new" aria-hidden="true">NEW</span>
					</button>
				))}
		</div>
	)
}
